class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  /* Phase D Q-05: die Option IST jetzt der Link (a role=option) bzw. der Knopf „Nach … suchen"
     (sections/predictive-search.liquid) — gewaehlt heisst [role=option][aria-selected=true]. */
  selectedOption() {
    return this.querySelector('[role="option"][aria-selected="true"]');
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.selectedOption()) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    // Enter auf einem gewaehlten Treffer (Phase D Q-05): das Formular wird gar nicht erst
    // abgeschickt — onKeyup oeffnet den Treffer (selectOption). Vorher entschied der
    // Absende-Weg das ueber den Waechter „[aria-selected] a" (hier und in ap-search-smart.js);
    // ohne Absenden gibt es kein Wettrennen zwischen Treffer und Groessenroute mehr.
    if ((event.code === 'Enter' || event.key === 'Enter') && this.selectedOption()) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // Nachpruefung Code-Suche 21.10.2026: steht der alte Begriff NICHT im Knopf, lieferte match()
      // null, onChange brach mit einem TypeError ab und die Vorschau fragte danach nichts mehr - so,
      // wenn der Kunde das Praefix der Feldsuche selbst tippt („variants.sku:G“: der Knopf nennt nur
      // „G“, sections/predictive-search.liquid). Dann den Text nicht anfassen; die Antwort setzt ihn.
      const treffer = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (!treffer || treffer.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.selectedOption();

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    // Phase D Q-05: alle Optionen (Treffer-Links und „Nach … suchen"), nicht mehr die li.
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    // Die gewaehlte Zeile ins Bild des Ergebnisfelds holen (Phase D Q-05): das Feld scrollt,
    // der Fokus bleibt im Eingabefeld — vorher lief die Auswahl unter den sichtbaren Rand.
    if (typeof activeElement.scrollIntoView === 'function') activeElement.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.selectedOption();

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // SUCHFELDER (Kurz-SKU Stufe 2, Betreiber 29.09.2026): nach dem Umschalten ist die SKU
    // jedes Produkts die kurze Artikelnummer, die EAN steht nur noch im Barcode. Shopifys
    // dokumentierter Standard der Vorhersagesuche ist title, product_type, variants.title,
    // vendor — ohne SKU und Barcode. Live gemessen 29.09.2026 (beide Shops): die PRODUKTsuche
    // findet SKU und Barcode trotzdem (EAN nur im Barcode, UUID nur in der SKU) und wertet die
    // Liste nicht aus; fuer Seiten/Beitraege wertet Shopify sie aus — mit body belegten Seiten
    // wie „Ueber uns" Produktplaetze (16/36 Anfragen anders). Diese Liste = Standard + SKU +
    // Barcode: 36/36 heutige Anfragen gleich (reifenfair.de und reifen-b2b.de) und haelt EAN und
    // Artikelnummer fest, falls Shopify die Liste kuenftig auch fuer Produkte auswertet.
    // Die Suchseite hat ihre eigene Liste (snippets/ap-suchfelder.liquid).
    const suchfelder = 'title,product_type,variants.title,vendor,variants.sku,variants.barcode';

    // CODE-SUCHE (Betreiber-Ja 21.10.2026, Regel und Messbefund in assets/ap-search-smart.js):
    // ist die GANZE Eingabe eine Artikelnummer oder EAN, fragt die Vorschau die Feldsuche
    // (variants.sku:<NR> bzw. variants.barcode:<GTIN> OR variants.sku:<GTIN> - dieselbe Anfrage
    // wie beim Absenden, window.apArtikelnummer.anfrage) und zeigt ausverkaufte Treffer am Ende,
    // statt sie auszublenden — nach dem SKU-Wechsel fand die Freitextsuche rund 57 % der
    // umgestellten Produkte nicht. searchTerm ist schon zusammengezogen (getQuery-Haken dort).
    // Ohne ap-search-smart.js und fuer jede andere Eingabe bleibt die Anfrage, wie sie war.
    const nummern = window.apArtikelnummer;
    const code =
      nummern && typeof nummern.code === 'function' && typeof nummern.anfrage === 'function'
        ? nummern.code(searchTerm)
        : null;
    const anfrage = code ? nummern.anfrage(code) : searchTerm;

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(anfrage)}&section_id=predictive-search` +
        `&resources%5Boptions%5D%5Bfields%5D=${encodeURIComponent(suchfelder)}` +
        (code ? '&resources%5Boptions%5D%5Bunavailable_products%5D=last' : ''),
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
